import { createContext, useContext, type ReactNode } from "react";

/**
 * ONE CLOCK PER SURFACE.
 *
 * A receipt's age is "how long ago did this sync", and the answer depends on
 * WHEN you are asking. Live, that is now. On a surface showing a snapshot it
 * is not: the notices deck anchors every age it draws to
 * `useNoticesReach().derivedAt` — the instant the rows were derived — and
 * prints that instant above the card ("As of 9h ago · reconnect to read or
 * snooze"). This chip used to keep its own `Date.now()` underneath, so a
 * record synced 30 hours before the snapshot read "1d" under a banner saying
 * the whole screen was 9 hours old. Both numbers true, the pair a bug.
 *
 * They also drift while the screen is open: `NoticesStaleness` re-renders
 * itself on a one-minute tick and offline nothing re-renders the deck, so the
 * banner advances and the receipt does not.
 *
 * CONTEXT, NOT A PROP, because the chip is two components below the surface —
 * `RecordCard` renders it — and a card has no opinion about time. Threading a
 * clock through it would put the seam in a component that does not care, and
 * every other host of the card would have to pass a clock it does not have.
 * `null` is "no surface clock", which reads live, so nothing outside a
 * provider changes behaviour.
 */
const RecordClockContext = createContext<number | null>(null);

export function RecordClockProvider({
  asOf,
  children,
}: {
  /** The instant this surface is showing, or null/undefined for "live". */
  asOf?: Date | number | null;
  children: ReactNode;
}) {
  return (
    <RecordClockContext.Provider value={toMillis(asOf)}>{children}</RecordClockContext.Provider>
  );
}

/**
 * The clock a record-scoped component should measure against: an explicit
 * `now` first, then the surface's, then the live one.
 */
export function useRecordClock(now?: Date | number | null): number {
  const surface = useContext(RecordClockContext);
  return toMillis(now) ?? surface ?? Date.now();
}

/** A `Date`, an ISO string off the wire, or epoch millis → millis; else null. */
export function toMillis(value: unknown): number | null {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim()) {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

